import { useState, useEffect } from 'react';
import { Spin, notification } from 'antd';
import {
  AimOutlined, BookOutlined, BulbOutlined, CalendarOutlined, GlobalOutlined, HeartOutlined,
  InfoCircleOutlined, QuestionCircleOutlined, ReadOutlined, StarOutlined, TeamOutlined,
  BankOutlined, HomeOutlined, PhoneOutlined, EnvironmentOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../../config';
import { M, G } from '../shared/constants';
import { authHeader, useLang, ssGet, ssSet } from '../shared/utils';
import { SammelanTab, GotraDirectoryTab } from './Village';
import { SamajParichayTab, VivahParichayTab } from './Vivah';

export function CombinedServicesTab({ profile, initialSub, onOpenChat }) {
  const { t } = useLang();
  const [sub, setSub] = useState(initialSub || 'events');

  const SERVICES = [
    { key: 'events',     label: t('कार्यक्रम',  'Events'),        Icon: CalendarOutlined },
    { key: 'identity',   label: t('हमारी पहचान','Our Identity'),  Icon: BankOutlined     },
    { key: 'vivah',      label: t('विवाह',       'Vivah'),         Icon: HeartOutlined    },
    { key: 'gotra',      label: t('गोत्र',       'Gotra'),         Icon: ReadOutlined     },
    { key: 'properties', label: t('भवन / गुरुकुल','Bhawan'),       Icon: HomeOutlined     },
  ];

  return (
    <div>
      {/* Clean underline tab strip */}
      <div style={{
        display: 'flex', overflowX: 'auto', scrollbarWidth: 'none',
        borderBottom: '1.5px solid #e5e7eb', marginBottom: 16,
        gap: 0,
      }}>
        {SERVICES.map(({ key, label, Icon }) => {
          const active = sub === key;
          return (
            <button key={key} onClick={() => setSub(key)} style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '10px 14px', border: 'none', background: 'none',
              cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap',
              fontWeight: active ? 700 : 500, fontSize: 12,
              color: active ? M : '#6b7280',
              borderBottom: active ? `2.5px solid ${M}` : '2.5px solid transparent',
              marginBottom: '-1.5px',
              transition: 'color 0.15s',
            }}>
              <Icon style={{ fontSize: 14, color: active ? M : '#9ca3af' }} />
              {label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {sub === 'events'     && <SammelanTab />}
      {sub === 'identity'   && <SamajParichayTab />}
      {sub === 'vivah'      && <VivahParichayTab profile={profile} onOpenChat={onOpenChat} />}
      {sub === 'gotra'      && <GotraDirectoryTab profile={profile} />}
      {sub === 'properties' && <SamajPropertiesTab />}
    </div>
  );
}

// ── Samaj Properties Tab ─────────────────────────────────────────────────────
const PROP_TYPES = [
  { key: 'samaj_bhawan', labelHi: 'समाज भवन',  labelEn: 'Samaj Bhawan', color: '#7a0000' },
  { key: 'chhatrawas',   labelHi: 'छात्रावास',   labelEn: 'Chhatrawas',   color: '#0891b2' },
  { key: 'gurukul',      labelHi: 'गुरुकुल',     labelEn: 'Gurukul',      color: '#15803d' },
];
const REGION_LABELS = { rajasthan: 'राजस्थान', other_state: 'अन्य प्रदेश', abroad: 'देशावर / परदेश' };

export function SamajPropertiesTab() {
  const { t, lang } = useLang();
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeTab, setTypeTab] = useState('samaj_bhawan');
  const [regionF, setRegionF] = useState('all');
  const [search,  setSearch]  = useState('');

  useEffect(() => {
    const CK = 'samaj_properties';
    const cached = ssGet(CK, 5 * 60 * 1000);
    if (cached && cached.length > 0) { setItems(cached); setLoading(false); return; }
    axios.get(`${baseUrl}/member/samaj-properties`, { headers: authHeader() })
      .then(r => { if (r.data.status) { setItems(r.data.result || []); ssSet(CK, r.data.result || []); } })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = items.filter(i => {
    if (i.property_type !== typeTab) return false;
    if (regionF !== 'all' && i.region_category !== regionF) return false;
    if (search) {
      const q = search.toLowerCase();
      return (i.city||'').toLowerCase().includes(q) ||
             (i.district||'').toLowerCase().includes(q) ||
             (i.address||'').toLowerCase().includes(q) ||
             (i.state||'').toLowerCase().includes(q);
    }
    return true;
  });

  // Group by state/region for display
  const grouped = filtered.reduce((acc, item) => {
    const key = item.region_category === 'rajasthan' ? (item.district || 'अन्य')
              : item.region_category === 'abroad'    ? (item.state || 'विदेश')
              : (item.state || 'अन्य');
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  const typeInfo = PROP_TYPES.find(t => t.key === typeTab);
  const regions  = [...new Set(items.filter(i => i.property_type === typeTab).map(i => i.region_category))];

  return (
    <div>
      {/* Header */}
      <div style={{ background: `linear-gradient(135deg,${M},#b71c1c)`, borderRadius: 14, padding: '14px 16px', marginBottom: 14 }}>
        <div style={{ fontSize: 17, fontWeight: 900, color: '#fff' }}>🏛️ {t('समाज भवन / गुरुकुल / छात्रावास', 'Samaj Bhawan / Gurukul / Chhatrawas')}</div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', marginTop: 3 }}>
          {t('राजपुरोहित समाज की सामुदायिक संपत्तियाँ', 'Rajpurohit community buildings across India')}
        </div>
      </div>

      {/* Type pills */}
      <div style={{ display: 'flex', gap: 7, marginBottom: 12, flexWrap: 'wrap' }}>
        {PROP_TYPES.map(pt => (
          <button key={pt.key} onClick={() => { setTypeTab(pt.key); setRegionF('all'); setSearch(''); }}
            style={{ border: `2px solid ${pt.color}`, borderRadius: 20, padding: '5px 14px', fontSize: 12,
              fontWeight: 700, cursor: 'pointer',
              background: typeTab === pt.key ? pt.color : 'transparent',
              color: typeTab === pt.key ? '#fff' : pt.color }}>
            {lang === 'en' ? pt.labelEn : pt.labelHi}
          </button>
        ))}
      </div>

      {/* Region filter */}
      {regions.length > 1 && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
          {['all', ...regions].map(r => (
            <button key={r} onClick={() => setRegionF(r)}
              style={{ fontSize: 10, fontWeight: 700, padding: '4px 11px', borderRadius: 20, cursor: 'pointer',
                background: regionF === r ? M : '#f3f4f6',
                color: regionF === r ? '#fff' : '#374151',
                border: `1px solid ${regionF === r ? M : '#e5e7eb'}` }}>
              {r === 'all' ? t('सभी', 'All') : REGION_LABELS[r] || r}
            </button>
          ))}
        </div>
      )}

      {/* Search */}
      <div style={{ marginBottom: 12 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('शहर, जिला खोजें...','Search city, district...')}
          style={{ width: '100%', padding: '8px 12px', border: `1.5px solid ${M}25`, borderRadius: 9, fontSize: 13, boxSizing: 'border-box' }} />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : Object.keys(grouped).length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
          {search ? t('कोई परिणाम नहीं','No results') : t('अभी कोई जानकारी उपलब्ध नहीं','No entries yet')}
        </div>
      ) : (
        Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b, 'hi')).map(([groupName, list]) => (
          <div key={groupName} style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: typeInfo?.color || M, marginBottom: 8,
              paddingBottom: 5, borderBottom: `2px solid ${typeInfo?.color || M}20`,
              display: 'flex', alignItems: 'center', gap: 6 }}>
              <EnvironmentOutlined style={{ fontSize: 13 }} />
              {groupName} <span style={{ fontWeight: 500, color: '#9ca3af', marginLeft: 4 }}>({list.length})</span>
            </div>
            {list.map(item => (
              <div key={item.id} style={{
                background: '#fff', borderRadius: 12, marginBottom: 8,
                border: '1px solid #e5e7eb', boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
                overflow: 'hidden', display: 'flex',
              }}>
                {item.photo_url && (
                  <img src={item.photo_url} alt="" style={{ width: 80, objectFit: 'cover', flexShrink: 0 }} />
                )}
                <div style={{ flex: 1, padding: '11px 13px', minWidth: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: 14, color: '#1f2937' }}>
                    {item.name || item.city}
                    {item.name && <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 500, marginLeft: 6 }}>{item.city}</span>}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3, lineHeight: 1.5 }}>{item.address}</div>
                  {item.established_year && (
                    <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>
                      {t('स्थापना','Est.')} {item.established_year}
                      {item.is_residential ? <span style={{ marginLeft: 8, background: '#dcfce7', color: '#15803d', borderRadius: 10, padding: '1px 7px', fontWeight: 700 }}>पूर्ण आवासीय</span> : null}
                    </div>
                  )}
                  {item.contact_phone && (
                    <a href={`tel:${item.contact_phone}`}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700,
                        color: '#15803d', background: '#dcfce7', borderRadius: 10, padding: '3px 10px', marginTop: 6, textDecoration: 'none' }}>
                      <PhoneOutlined /> {item.contact_phone}
                    </a>
                  )}
                  {item.map_link && (
                    <a href={item.map_link} target="_blank" rel="noopener noreferrer"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700,
                        color: '#2563eb', background: '#eff6ff', borderRadius: 10, padding: '3px 10px', marginTop: 6, marginLeft: 6, textDecoration: 'none' }}>
                      <EnvironmentOutlined /> Map
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
}

// ── Sammelan / Events Tab ─────────────────────────────────────────────────────
const PLATFORM_META = {
  zoom:    { icon: '🎥', label: 'Zoom',         color: '#2D8CFF' },
  meet:    { icon: '📹', label: 'Google Meet',  color: '#00832D' },
  youtube: { icon: '▶️', label: 'YouTube Live', color: '#FF0000' },
  webex:   { icon: '💻', label: 'Webex',        color: '#00BCEB' },
  teams:   { icon: '🟣', label: 'MS Teams',     color: '#5C2D91' },
};

function fmtEventDate(dateStr, timeStr, userTz) {
  if (!dateStr) return '—';
  try {
    const base = timeStr ? `${dateStr}T${timeStr}` : `${dateStr}T00:00:00`;
    const ist  = new Date(base + '+05:30'); // events are stored in IST
    const opts = { day: 'numeric', month: 'short', year: 'numeric', weekday: 'short' };
    const dateOut = ist.toLocaleDateString('en-IN', opts);
    if (!timeStr) return dateOut;
    const timeOpts = { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' };
    const istTime  = ist.toLocaleTimeString('en-IN', { ...timeOpts, timeZone: 'Asia/Kolkata' });
    if (!userTz || userTz === 'Asia/Kolkata') return `${dateOut} · ${istTime}`;
    const localTime = ist.toLocaleTimeString('en-IN', { ...timeOpts, timeZone: userTz });
    return `${dateOut} · ${istTime} (${localTime})`;
  } catch { return dateStr; }
}

function EventCard({ ev, lang, userTz }) {
  const t    = (hi, en) => lang === 'en' ? en : hi;
  const pm   = PLATFORM_META[ev.virtual_platform?.toLowerCase()] || {};
  const isVirtual  = ev.event_type === 'virtual';
  const isHybrid   = ev.event_type === 'hybrid';
  const isPhysical = ev.event_type === 'physical';
  const daysAway   = Number(ev.days_away);
  const isPast     = daysAway < -1;
  const isToday    = daysAway === 0;
  const isTomorrow = daysAway === 1;
  const isSoon     = daysAway > 0 && daysAway <= 7;

  const typeColor  = isVirtual ? '#1d4ed8' : isHybrid ? '#7c3aed' : M;
  const typeBg     = isVirtual ? '#eff6ff' : isHybrid ? '#f5f3ff' : '#fff5f5';
  const typeLabel  = isVirtual ? t('ऑनलाइन', 'Online') : isHybrid ? t('हाइब्रिड', 'Hybrid') : t('स्थानीय', 'In-Person');
  const typeIcon   = isVirtual ? '🌐' : isHybrid ? '🔀' : '📍';

  const title = (lang === 'en' && ev.title_en) ? ev.title_en : ev.title;
  const desc  = (lang === 'en' && ev.description_en) ? ev.description_en : ev.description;

  return (
    <div style={{
      background: '#fff', borderRadius: 14, marginBottom: 12,
      border: `1.5px solid ${isPast ? '#e5e7eb' : typeColor + '40'}`,
      overflow: 'hidden', opacity: isPast ? 0.72 : 1,
      boxShadow: isPast ? 'none' : `0 3px 12px ${typeColor}18`,
    }}>
      {/* Banner image */}
      {ev.banner_image && (
        <img src={ev.banner_image} alt={title} style={{ width: '100%', maxHeight: 160, objectFit: 'cover', display: 'block' }} />
      )}

      <div style={{ padding: '12px 14px' }}>
        {/* Type + urgency badges */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          <span style={{ fontSize: 10, fontWeight: 800, background: typeBg, color: typeColor, border: `1px solid ${typeColor}30`, borderRadius: 20, padding: '2px 8px' }}>
            {typeIcon} {typeLabel}
          </span>
          {isToday   && <span style={{ fontSize: 10, fontWeight: 800, background: '#fef3c7', color: '#92400e', border: '1px solid #fbbf24', borderRadius: 20, padding: '2px 8px' }}>🔔 {t('आज', 'TODAY')}</span>}
          {isTomorrow&& <span style={{ fontSize: 10, fontWeight: 800, background: '#ecfdf5', color: '#065f46', border: '1px solid #34d399', borderRadius: 20, padding: '2px 8px' }}>⏰ {t('कल', 'TOMORROW')}</span>}
          {isSoon && !isToday && !isTomorrow && <span style={{ fontSize: 10, fontWeight: 700, background: '#eff6ff', color: '#1d4ed8', borderRadius: 20, padding: '2px 8px' }}>{daysAway} {t('दिन बाद', 'days away')}</span>}
          {isPast    && <span style={{ fontSize: 10, color: '#9ca3af', borderRadius: 20, padding: '2px 8px', background: '#f9fafb' }}>{t('समाप्त', 'Past')}</span>}
          {pm.icon && <span style={{ fontSize: 10, fontWeight: 700, background: pm.color + '15', color: pm.color, borderRadius: 20, padding: '2px 8px', border: `1px solid ${pm.color}30` }}>{pm.icon} {pm.label}</span>}
        </div>

        {/* Title */}
        <div style={{ fontSize: 15, fontWeight: 900, color: '#111827', lineHeight: 1.3, marginBottom: 6 }}>{title}</div>

        {/* Date/time — timezone aware */}
        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6, display: 'flex', alignItems: 'flex-start', gap: 5 }}>
          <span>📅</span>
          <span style={{ lineHeight: 1.5 }}>{fmtEventDate(ev.event_date, ev.event_time, userTz)}</span>
        </div>

        {/* Location / Virtual info */}
        {(isPhysical || isHybrid) && ev.location && (
          <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6, display: 'flex', gap: 5 }}>
            <span>📍</span><span>{ev.location}</span>
          </div>
        )}

        {/* Description */}
        {desc && <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6, marginBottom: 10 }}>{desc}</div>}

        {/* Virtual join button */}
        {(isVirtual || isHybrid) && ev.virtual_link && !isPast && (
          <a href={ev.virtual_link} target="_blank" rel="noopener noreferrer" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            background: pm.color || typeColor, color: '#fff', borderRadius: 10,
            padding: '10px 0', fontSize: 13, fontWeight: 800, textDecoration: 'none',
            boxShadow: `0 3px 10px ${(pm.color || typeColor)}40`,
          }}>
            {pm.icon || '🌐'} {t('अभी Join करें', 'Join Now')} — {pm.label || t('ऑनलाइन', 'Online')}
          </a>
        )}

        {/* Share event */}
        {!isPast && (
          <button onClick={() => {
            const text = `${title}\n📅 ${fmtEventDate(ev.event_date, ev.event_time, null)}\n${ev.virtual_link ? `🌐 ${ev.virtual_link}` : (ev.location ? `📍 ${ev.location}` : '')}`;
            if (navigator.share) navigator.share({ title, text }).catch(() => {});
            else navigator.clipboard?.writeText(text);
          }} style={{ marginTop: 8, width: '100%', background: 'none', border: `1px solid ${typeColor}40`, borderRadius: 10, padding: '7px 0', fontSize: 11, fontWeight: 700, color: typeColor, cursor: 'pointer' }}>
            🔗 {t('Share करें', 'Share Event')}
          </button>
        )}
      </div>
    </div>
  );
}

